// TransferModal.tsx — el paso AI Transfer a pantalla grande.
// Qué hace: elegir la REFERENCIA de anuncio (una) y VARIAS fotos propias, más el
//   toggle de logo. Reusa el armazón del modal de estilos.
// Qué exporta: TransferModal.
// Quién lo usa: widgets/TransferPicker.tsx.
//
// Por qué varias fotos: el servidor ya rota las fotos post por post
// (bakedPhotoUrls[bakedPhotoIndex++ % length] en routes/generate.ts) y recibe un
// array. El límite de UNA era del cliente, que reemplazaba la selección en vez de
// acumularla — así que generar 5 posts daba 5 veces la misma foto.

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { BakedStyleOption } from '../types';
import {
  Overlay, Panel, Head, HeadText, CloseBtn, Body, GridPane, StyleGrid, StyleCard,
  StyleName, PickBadge, SidePane, OptionRow, PhotoGrid, PhotoCell,
  PhotoCheck, EmptyGrid, Foot, FootCount, FootActions, GhostBtn, PrimaryBtn,
  Formula, FormulaItem, FormulaThumb, FormulaCaption, FormulaOp, FormulaNote,
  StepHead, StepNum, StepTitle, UploadTile, SectionTabs, SectionTab, TabCount,
  NoteArea, NoteChips, NoteChip, NoteCount,
} from '../styleModalStyled';

interface Props {
  open: boolean;
  onClose: () => void;
  bakedStyles: BakedStyleOption[];
  transferRefUrl: string;
  selectReference: (url: string) => void;
  uploadReference: (file: File) => void;
  /** Sube una foto propia y la deja seleccionada (sin salir del modal). */
  uploadPhoto: (file: File) => void;
  photoUploading: boolean;
  /** Ads ya generados en este workspace: sirven de referencia igual que el
   *  catálogo, y suelen ser mejores porque ya son de la marca. */
  myAds: { id: string; url: string; label: string }[];
  refUploading: boolean;
  myPhotos: { id: string; url: string; name?: string }[];
  selectedPhotoIds: string[];
  togglePhoto: (id: string) => void;
  useLogo: boolean;
  setUseLogo: (v: boolean) => void;
  hasLogo: boolean;
  confirmTransfer: () => void;
  /** Cuántos posts se van a generar — define cuántas fotos conviene elegir. */
  totalPosts: number;
  /** "Vary layout": una composición distinta por anuncio, mismo lenguaje visual.
   *  ON por defecto — sin esto, pedir 10 anuncios devolvía 10 casi idénticos. */
  varyLayout: boolean;
  setVaryLayout: (v: boolean) => void;
  /** Indicaciones libres para ESTA tanda ("agregá sólo mi marca al producto").
   *  Opcional: vacío = el AI Transfer trabaja como siempre. */
  instructions: string;
  setInstructions: (v: string) => void;
}

/** Tope del campo de indicaciones. No es un límite técnico del modelo: pasada
 *  esa longitud la indicación deja de ser una indicación y compite con el resto
 *  del prompt (marca, contacto, composición), que es cuando el resultado empeora. */
export const INSTRUCTIONS_MAX = 300;

// Ejemplos tocables: escribir la indicación desde cero es la barrera real. El
// chip lleva una etiqueta CORTA (si no, cada uno se come un renglón y empuja el
// campo fuera de la parte visible del panel) y lo que inserta es la frase
// completa, que es lo que el modelo necesita. El primero es el que pidió Charly.
const INSTRUCTION_EXAMPLES: Array<{ label: string; text: string }> = [
  { label: 'Only my brand', text: 'Add only my brand to the product' },
  { label: 'Plain background', text: 'Show the product on a clean plain background' },
  { label: 'No people', text: 'No people in the image' },
  { label: 'Empty bottom', text: 'Leave the bottom third empty' },
];

const Check = ({ size = 13 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M2.5 6.2l2.3 2.3 4.7-5" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export function TransferModal({
  open, onClose, bakedStyles, transferRefUrl, selectReference, uploadReference,
  uploadPhoto, photoUploading, myAds, refUploading, myPhotos, selectedPhotoIds, togglePhoto, useLogo, setUseLogo,
  hasLogo, confirmTransfer, totalPosts, varyLayout, setVaryLayout,
  instructions, setInstructions,
}: Props) {
  const [uploadError, setUploadError] = useState('');
  // Origen de la referencia. Arranca en los ads propios cuando los hay: ya son
  // de la marca, así que es el mejor punto de partida.
  const [refSource, setRefSource] = useState<'mine' | 'catalog'>('catalog');
  useEffect(() => { if (open) setRefSource(myAds.length > 0 ? 'mine' : 'catalog'); }, [open, myAds.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const refs = bakedStyles.filter((s) => s.preview);
  const isUploadedRef = transferRefUrl && !refs.some((s) => s.preview === transferRefUrl);
  const nPhotos = selectedPhotoIds.length;
  const firstPhoto = myPhotos.find((p) => selectedPhotoIds.includes(p.id));

  // Con más posts que fotos, alguna se repite: se dice, en vez de que el usuario
  // lo descubra mirando los resultados.
  const varietyHint = nPhotos === 0
    ? `No photo picked — the AI will build every image from the reference.`
    : nPhotos >= totalPosts
      ? `Each of your ${totalPosts} post${totalPosts !== 1 ? 's' : ''} gets a different photo.`
      : `${nPhotos} photo${nPhotos !== 1 ? 's' : ''} for ${totalPosts} posts — they cycle, so some repeat. Pick ${totalPosts - nPhotos} more for one each.`;

  return createPortal(
    <Overlay onClick={onClose} role="dialog" aria-modal="true" aria-label="AI Transfer">
      {/* Portal a body: el chat vive dentro de contenedores con transform, que
          atrapan a position:fixed (mismo caso que el modal de estilos). */}
      <Panel onClick={(e) => e.stopPropagation()}>
        <Head>
          <HeadText>
            <h2>{varyLayout ? 'Borrow this ad’s look' : 'Copy this ad’s design'}</h2>
            <p>
              {varyLayout
                ? 'Pick one reference — the AI keeps its look and lays each ad out differently — and your own photos to feature.'
                : 'Pick one reference — the AI copies its layout — and your own photos to feature.'}
            </p>
          </HeadText>
          <CloseBtn type="button" onClick={onClose} aria-label="Close">
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            </svg>
          </CloseBtn>
        </Head>

        <Body>
          <GridPane>
            {/* La fórmula con las imágenes REALES que se van eligiendo: entender
                "referencia + mi foto = mi anuncio" es el único concepto del paso. */}
            <Formula>
              <FormulaItem>
                <FormulaThumb $filled={!!transferRefUrl} $accent={!transferRefUrl}>
                  {transferRefUrl ? <img src={transferRefUrl} alt="" /> : '1'}
                </FormulaThumb>
                <FormulaCaption $done={!!transferRefUrl}>
                  {transferRefUrl ? 'Reference ✓' : 'Pick a reference'}
                </FormulaCaption>
              </FormulaItem>

              <FormulaOp>+</FormulaOp>

              <FormulaItem>
                <FormulaThumb $filled={nPhotos > 0}>
                  {firstPhoto ? <img src={firstPhoto.url} alt="" /> : '2'}
                </FormulaThumb>
                <FormulaCaption $done={nPhotos > 0}>
                  {nPhotos > 0 ? `${nPhotos} photo${nPhotos !== 1 ? 's' : ''} ✓` : 'Your photo (optional)'}
                </FormulaCaption>
              </FormulaItem>

              <FormulaOp>=</FormulaOp>

              <FormulaItem>
                <FormulaThumb $filled={false}>✨</FormulaThumb>
                <FormulaCaption>Your ad</FormulaCaption>
              </FormulaItem>

              <FormulaNote>
                {varyLayout ? (
                  <>
                    The AI keeps the reference&apos;s <strong>look</strong> and gives each ad its own{' '}
                    <strong>layout</strong>. Your photos go in <strong>untouched</strong>.
                  </>
                ) : (
                  <>
                    The AI copies the <strong>layout</strong> of the reference and rebuilds it for your
                    brand. Your photos go in <strong>untouched</strong>.
                  </>
                )}
              </FormulaNote>
            </Formula>

            <StepHead>
              <StepNum $done={!!transferRefUrl}>1</StepNum>
              <StepTitle>Pick a reference ad <span>— required</span></StepTitle>
            </StepHead>
            {/* Pestañas en vez de secciones apiladas: con 40 ads propios y 35
                estilos, pasar de una categoría a la otra era todo scroll. */}
            {myAds.length > 0 && (
              <SectionTabs>
                <SectionTab type="button" $active={refSource === 'mine'} onClick={() => setRefSource('mine')}>
                  From your ads <TabCount $active={refSource === 'mine'}>{myAds.length}</TabCount>
                </SectionTab>
                <SectionTab type="button" $active={refSource === 'catalog'} onClick={() => setRefSource('catalog')}>
                  Style catalog <TabCount $active={refSource === 'catalog'}>{refs.length}</TabCount>
                </SectionTab>
              </SectionTabs>
            )}

            {refSource === 'mine' && myAds.length > 0 && (
              <StyleGrid>
                <UploadTile $tall title="Upload an ad you like as the reference">
                  <span className="plus">＋</span>
                  {refUploading ? 'Uploading…' : 'Upload my own ad'}
                  <input
                    type="file" accept="image/*" disabled={refUploading}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (!f) return;
                      if (!f.type.startsWith('image/')) { setUploadError('That file is not an image.'); return; }
                      setUploadError('');
                      uploadReference(f);
                    }}
                  />
                </UploadTile>
                {myAds.map((ad) => {
                  const active = transferRefUrl === ad.url;
                  return (
                    <StyleCard
                      key={ad.id} type="button" $active={active} aria-pressed={active}
                      title="Use this ad as the reference"
                      onClick={() => selectReference(ad.url)}
                    >
                      <img src={ad.url} alt="" loading="lazy" />
                      <StyleName style={{ textTransform: 'capitalize' }}>{ad.label}</StyleName>
                      {active && <PickBadge><Check /></PickBadge>}
                    </StyleCard>
                  );
                })}
              </StyleGrid>
            )}

            {refSource === 'catalog' && (refs.length === 0 ? (
              <EmptyGrid>No reference ads available yet.</EmptyGrid>
            ) : (
              <StyleGrid>
                <UploadTile $tall title="Upload an ad you like as the reference">
                  <span className="plus">＋</span>
                  {refUploading ? 'Uploading…' : 'Upload my own ad'}
                  <input
                    type="file" accept="image/*" disabled={refUploading}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (!f) return;
                      if (!f.type.startsWith('image/')) { setUploadError('That file is not an image.'); return; }
                      setUploadError('');
                      uploadReference(f);
                    }}
                  />
                </UploadTile>
                {refs.map((s) => {
                  const active = transferRefUrl === s.preview;
                  return (
                    <StyleCard
                      key={s.id}
                      type="button"
                      $active={active}
                      aria-pressed={active}
                      title={s.label}
                      onClick={() => selectReference(s.preview)}
                    >
                      <img src={s.preview} alt="" loading="lazy" />
                      <StyleName>{s.label}</StyleName>
                      {active && <PickBadge><Check /></PickBadge>}
                    </StyleCard>
                  );
                })}
              </StyleGrid>
            ))}

            {uploadError && (
              <div style={{ fontSize: 12, color: '#B91C1C', marginTop: 6 }}>{uploadError}</div>
            )}
            {isUploadedRef && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 6 }}>Your reference:</div>
                <img
                  src={transferRefUrl} alt="Uploaded reference"
                  style={{ width: 110, aspectRatio: '3 / 4', objectFit: 'cover', borderRadius: 10, border: '2px solid #2563EB' }}
                />
              </div>
            )}
          </GridPane>

          <SidePane>
            <StepHead>
              <StepNum $done={nPhotos > 0}>2</StepNum>
              <StepTitle>Your photos <span>— optional</span></StepTitle>
            </StepHead>
            <div style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.5, marginBottom: 10 }}>
              Used exactly as they are. Pick several and each post gets a different one.
            </div>

            {(
              <>
                <PhotoGrid>
                  {/* Subir va PRIMERO: escondido al final de la grilla no se
                      encontraba, y sin esto había que salir del flujo e ir a
                      Brand → Assets para agregar una foto. */}
                  <UploadTile title="Upload a photo from your computer">
                    <span className="plus">＋</span>
                    {photoUploading ? 'Uploading…' : 'Upload'}
                    <input
                      type="file" accept="image/*" disabled={photoUploading}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = '';
                        if (!f) return;
                        if (!f.type.startsWith('image/')) { setUploadError('That file is not an image.'); return; }
                        setUploadError('');
                        uploadPhoto(f);
                      }}
                    />
                  </UploadTile>
                  {myPhotos.map((ph) => {
                    const sel = selectedPhotoIds.includes(ph.id);
                    return (
                      <PhotoCell
                        key={ph.id} type="button" $active={sel} aria-pressed={sel}
                        title={ph.name || 'Photo'} onClick={() => togglePhoto(ph.id)}
                      >
                        <img src={ph.url} alt="" loading="lazy" />
                        {sel && <PhotoCheck><Check size={11} /></PhotoCheck>}
                      </PhotoCell>
                    );
                  })}
                </PhotoGrid>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 10, lineHeight: 1.5 }}>
                  {myPhotos.length === 0
                    ? 'No photos in this workspace yet — upload one above.'
                    : varietyHint}
                </div>
              </>
            )}

            {hasLogo && (
              <>
                <StepHead style={{ marginTop: 18 }}>
                  <StepNum $done={useLogo}>3</StepNum>
                  <StepTitle>Logo <span>— optional</span></StepTitle>
                </StepHead>
                <OptionRow>
                  <input type="checkbox" checked={useLogo} onChange={(e) => setUseLogo(e.target.checked)} />
                  <span><strong>Use my logo</strong> <em>— the AI works it into the ad.</em></span>
                </OptionRow>
              </>
            )}

            <StepHead style={{ marginTop: 18 }}>
              <StepNum $done={varyLayout}>{hasLogo ? 4 : 3}</StepNum>
              <StepTitle>Variety</StepTitle>
            </StepHead>
            <OptionRow>
              <input type="checkbox" checked={varyLayout} onChange={(e) => setVaryLayout(e.target.checked)} />
              <span>
                <strong>Vary the layout</strong>{' '}
                <em>— every ad keeps this reference&rsquo;s colours, type and mood, but arranges the photo, headline and contact differently.</em>
              </span>
            </OptionRow>
            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 8, lineHeight: 1.5 }}>
              {varyLayout
                ? totalPosts > 1
                  ? `Your ${totalPosts} ads will share the look but not the arrangement.`
                  : 'Turn this off to copy the reference layout exactly.'
                : 'Off — every ad copies this reference’s layout as closely as possible, so they will look nearly identical to each other.'}
            </div>

            <StepHead style={{ marginTop: 18 }}>
              <StepNum $done={instructions.trim().length > 0}>{hasLogo ? 5 : 4}</StepNum>
              <StepTitle>Instructions <span>— optional</span></StepTitle>
            </StepHead>
            <NoteChips style={{ marginTop: 0, marginBottom: 9 }}>
              {INSTRUCTION_EXAMPLES.map((ex) => (
                <NoteChip
                  key={ex.label}
                  type="button"
                  title={ex.text}
                  onClick={() => setInstructions(
                    ((instructions.trim() ? instructions.trim() + '. ' : '') + ex.text).slice(0, INSTRUCTIONS_MAX),
                  )}
                >
                  + {ex.label}
                </NoteChip>
              ))}
            </NoteChips>
            <NoteArea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value.slice(0, INSTRUCTIONS_MAX))}
              placeholder="Anything specific for these ads? Tap an example or write your own."
              aria-label="Extra instructions for the AI"
            />
            <NoteCount $over={instructions.length >= INSTRUCTIONS_MAX}>
              {instructions.length >= INSTRUCTIONS_MAX
                ? `${INSTRUCTIONS_MAX}/${INSTRUCTIONS_MAX} — that’s the limit; keep it to one clear request.`
                : `${instructions.length}/${INSTRUCTIONS_MAX} — one short, concrete request works best.`}
            </NoteCount>
          </SidePane>
        </Body>

        <Foot>
          <FootCount>
            {transferRefUrl
              ? <><strong>1</strong> reference · <strong>{nPhotos}</strong> photo{nPhotos !== 1 ? 's' : ''}</>
              : 'Pick a reference ad to continue'}
          </FootCount>
          <FootActions>
            <GhostBtn type="button" onClick={onClose}>Cancel</GhostBtn>
            <PrimaryBtn
              type="button"
              disabled={!transferRefUrl}
              onClick={() => { confirmTransfer(); onClose(); }}
            >
              {!transferRefUrl ? 'Pick a reference' : nPhotos === 0 ? 'Generate from reference' : 'Combine into ads'}
            </PrimaryBtn>
          </FootActions>
        </Foot>
      </Panel>
    </Overlay>,
    document.body
  );
}
